import { useState, useEffect, useRef } from 'react'
import { FileText, Download, Loader2, Sparkles, Printer } from 'lucide-react'
import { projectApi } from '../services/projectApi'
import { getAuthHeadersForStream } from '../services/api'
import type { Project } from '../types'

export function CxReport() {
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProject, setSelectedProject] = useState('')
  const [markdown, setMarkdown] = useState('')
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloadingDocx, setDownloadingDocx] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    projectApi.getAll({}).then((result) => setProjects(result.items)).catch(() => {})
  }, [])

  async function handleGenerate() {
    if (!selectedProject) return
    setGenerating(true)
    setError(null)
    setMarkdown('')

    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api'
      const authHeaders = await getAuthHeadersForStream()

      const response = await fetch(`${apiBase}/ai/cx-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ projectId: Number(selectedProject) }),
      })

      if (!response.ok) {
        const errText = await response.text()
        throw new Error(`Report generation failed: ${errText}`)
      }

      const contentType = response.headers.get('Content-Type') || ''
      if (contentType.includes('json')) {
        // Markdown returned as JSON (docx generation unavailable)
        const data = await response.json() as { markdown: string; error?: string }
        setMarkdown(data.markdown)
      } else {
        // Binary docx — download directly and show a message
        const blob = await response.blob()
        downloadBlob(blob, response.headers.get('Content-Disposition'))
        setMarkdown('*Report downloaded as Word document.*')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report')
    } finally {
      setGenerating(false)
    }
  }

  async function handleDownloadDocx() {
    if (!markdown) return
    setDownloadingDocx(true)
    try {
      const apiBase = import.meta.env.VITE_API_URL || '/api'
      const authHeaders = await getAuthHeadersForStream()
      const project = projects.find(p => String(p.id) === selectedProject)

      const response = await fetch(`${apiBase}/ai/generate-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          content: markdown,
          format: 'docx',
          filename: `Cx_Report_${project?.projectNumber || selectedProject}_${new Date().toISOString().split('T')[0]}.docx`,
        }),
      })

      if (!response.ok) {
        // Fallback: download as markdown
        const blob = new Blob([markdown], { type: 'text/markdown' })
        downloadBlob(blob, null, `Cx_Report_${project?.projectNumber || selectedProject}.md`)
        return
      }

      const blob = await response.blob()
      downloadBlob(blob, response.headers.get('Content-Disposition'), `Cx_Report_${project?.projectNumber || selectedProject}.docx`)
    } catch {
      // Fallback: download markdown
      const project = projects.find(p => String(p.id) === selectedProject)
      const blob = new Blob([markdown], { type: 'text/markdown' })
      downloadBlob(blob, null, `Cx_Report_${project?.projectNumber || selectedProject}.md`)
    } finally {
      setDownloadingDocx(false)
    }
  }

  function downloadBlob(blob: Blob, disposition: string | null, fallbackName?: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const match = disposition?.match(/filename="?([^"]+)"?/)
    a.download = match?.[1] || fallbackName || 'Cx_Report.docx'
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleDownloadMarkdown() {
    if (!markdown) return
    const project = projects.find(p => String(p.id) === selectedProject)
    const blob = new Blob([markdown], { type: 'text/markdown' })
    downloadBlob(blob, null, `Cx_Report_${project?.projectNumber || selectedProject}.md`)
  }

  // Simple markdown to HTML (headings, bold, lists, tables, hr)
  function renderMarkdown(md: string) {
    const lines = md.split('\n')
    const html: string[] = []
    let inList = false
    let inTable = false

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i] ?? ''

      // Horizontal rule
      if (/^---+$/.test(line.trim())) {
        if (inList) { html.push('</ul>'); inList = false }
        if (inTable) { html.push('</tbody></table>'); inTable = false }
        html.push('<hr/>')
        continue
      }

      // Table rows
      if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
        if (inList) { html.push('</ul>'); inList = false }
        const cells = line.split('|').slice(1, -1).map(c => c.trim())
        // Skip separator rows
        if (cells.every(c => /^[-:]+$/.test(c))) continue
        if (!inTable) {
          html.push('<table class="data-table" style="margin: 8px 0"><thead><tr>')
          cells.forEach(c => html.push(`<th>${escapeAndFormat(c)}</th>`))
          html.push('</tr></thead><tbody>')
          inTable = true
          continue
        }
        html.push('<tr>')
        cells.forEach(c => html.push(`<td>${escapeAndFormat(c)}</td>`))
        html.push('</tr>')
        continue
      }

      if (inTable) { html.push('</tbody></table>'); inTable = false }

      // Headings
      const headingMatch = line.match(/^(#{1,6})\s+(.+)/)
      if (headingMatch) {
        if (inList) { html.push('</ul>'); inList = false }
        const level = headingMatch[1]!.length
        html.push(`<h${level}>${escapeAndFormat(headingMatch[2] ?? '')}</h${level}>`)
        continue
      }

      // List items
      if (/^\s*[-*]\s+/.test(line)) {
        if (!inList) { html.push('<ul>'); inList = true }
        const content = line.replace(/^\s*[-*]\s+/, '')
        html.push(`<li>${escapeAndFormat(content)}</li>`)
        continue
      }

      if (inList) { html.push('</ul>'); inList = false }

      // Empty line
      if (!line.trim()) {
        html.push('<br/>')
        continue
      }

      // Paragraph
      html.push(`<p>${escapeAndFormat(line)}</p>`)
    }

    if (inList) html.push('</ul>')
    if (inTable) html.push('</tbody></table>')

    return html.join('\n')
  }

  function escapeAndFormat(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
  }

  const project = projects.find(p => String(p.id) === selectedProject)

  return (
    <div className="page-container animate-fade-in">
      <div className="page-header">
        <div>
          <h1>Commissioning Report</h1>
          <p className="page-subtitle">Generate an AI-powered Cx Report from project data</p>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-center gap-3 flex-wrap">
            <div className="form-group mb-0" style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
              <select
                className="form-select"
                value={selectedProject}
                onChange={(e) => { setSelectedProject(e.target.value); setMarkdown(''); setError(null) }}
              >
                <option value="">Select a project...</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {(p as unknown as { projectNumber?: string }).projectNumber} — {(p as unknown as { projectName?: string }).projectName || (p as unknown as { name?: string }).name}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={!selectedProject || generating}
            >
              {generating ? (
                <><Loader2 size={16} className="spin" /> Generating...</>
              ) : (
                <><Sparkles size={16} /> Generate Report</>
              )}
            </button>
            {markdown && (
              <>
                <button className="btn btn-secondary" onClick={handleDownloadDocx} disabled={downloadingDocx}>
                  <Download size={16} /> {downloadingDocx ? 'Converting...' : 'Download .docx'}
                </button>
                <button className="btn btn-ghost" onClick={handleDownloadMarkdown}>
                  <Download size={16} /> Download .md
                </button>
                <button className="btn btn-ghost" onClick={() => window.print()}>
                  <Printer size={16} /> Print / PDF
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="info-card danger mb-4">
          <p style={{ margin: 0, color: 'var(--accent-red)' }}>{error}</p>
        </div>
      )}

      {generating && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 'var(--spacing-10)' }}>
            <div className="ai-pulse" style={{ margin: '0 auto 16px' }}></div>
            <p className="text-secondary-color">
              Gathering project data and generating report for <strong>{project?.projectNumber}</strong>...
            </p>
            <p className="text-muted text-sm">This may take 30-60 seconds depending on project size.</p>
          </div>
        </div>
      )}

      {markdown && !generating && (
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-center gap-2">
              <FileText size={18} className="text-accent" />
              <h3>Report Preview</h3>
            </div>
          </div>
          <div
            ref={contentRef}
            className="card-body cx-report-content"
            style={{
              maxHeight: '70vh',
              overflowY: 'auto',
              lineHeight: 1.7,
              fontSize: 'var(--font-size-sm)',
            }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(markdown) }}
          />
        </div>
      )}

      {!markdown && !generating && !error && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 'var(--spacing-12)' }}>
            <FileText size={48} className="text-muted" style={{ marginBottom: 16 }} />
            <h3 className="text-secondary-color" style={{ marginBottom: 8 }}>Generate a Commissioning Report</h3>
            <p className="text-muted" style={{ maxWidth: 500, margin: '0 auto' }}>
              Select a project and click Generate. The AI will analyze all checklists, issues, submittals, and equipment data to produce a comprehensive Cx Report.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
